import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { AlertTriangle, XCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

interface ProviderSummary {
  provider: string
  count: number
}

interface DeactivationConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCount: number
  selectedMailboxes: string[]
  onConfirm: (reason: string) => void
}

const DEACTIVATION_REASONS = [
  'Provider shutdown - migrating to new provider',
  'Cost optimization',
  'Performance issues',
  'Contract ended',
  'Security concerns',
  'Business restructuring',
  'Custom...'
]

export function DeactivationConfirmDialog({
  open,
  onOpenChange,
  selectedCount,
  selectedMailboxes,
  onConfirm
}: DeactivationConfirmDialogProps) {
  const [reason, setReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [providerCostConfirmed, setProviderCostConfirmed] = useState(false)
  const [smartleadRemovalConfirmed, setSmartleadRemovalConfirmed] = useState(false)
  const [providerSummary, setProviderSummary] = useState<ProviderSummary[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch provider summary when dialog opens
  useEffect(() => {
    if (open && selectedMailboxes.length > 0) {
      fetchProviderSummary()
    }
  }, [open, selectedMailboxes])

  const fetchProviderSummary = async () => {
    setLoading(true)
    try {
      const { data: mailboxes, error } = await supabase
        .from('mailboxes')
        .select('provider')
        .in('id', selectedMailboxes)

      if (error) throw error

      // Group by provider and count
      const summary = mailboxes.reduce((acc: Record<string, number>, mailbox) => {
        const provider = mailbox.provider || 'Unknown'
        acc[provider] = (acc[provider] || 0) + 1
        return acc
      }, {})

      setProviderSummary(
        Object.entries(summary).map(([provider, count]) => ({
          provider,
          count
        }))
      )
    } catch (error) {
      console.error('Error fetching provider summary:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = () => {
    const finalReason = reason === 'Custom...' ? customReason : reason
    if (finalReason && providerCostConfirmed && smartleadRemovalConfirmed) {
      onConfirm(finalReason)
      handleClose()
    }
  }

  const handleClose = () => {
    setReason('')
    setCustomReason('')
    setProviderCostConfirmed(false)
    setSmartleadRemovalConfirmed(false)
    onOpenChange(false)
  }

  const isFormValid = reason && 
    (reason !== 'Custom...' || customReason.trim()) && 
    providerCostConfirmed && 
    smartleadRemovalConfirmed

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <XCircle className="w-5 h-5" />
            Confirm Mailbox Deactivation
          </DialogTitle>
          <DialogDescription>
            You are about to deactivate <strong>{selectedCount}</strong> mailboxes. 
            This action will mark them as DEACTIVATED and remove them from Smartlead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Provider Summary */}
          <div className="bg-muted/50 p-3 rounded-md">
            <h4 className="text-sm font-medium mb-2">Affected Mailbox Providers:</h4>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : (
              <div className="space-y-1">
                {providerSummary.map(({ provider, count }) => (
                  <div key={provider} className="flex justify-between text-sm">
                    <span>{provider}</span>
                    <span className="font-medium">{count} mailboxes</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deactivation Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Deactivation Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason for deactivation" />
              </SelectTrigger>
              <SelectContent>
                {DEACTIVATION_REASONS.map((reasonOption) => (
                  <SelectItem key={reasonOption} value={reasonOption}>
                    {reasonOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Reason Input */}
          {reason === 'Custom...' && (
            <div className="space-y-2">
              <Label htmlFor="customReason">Custom Reason *</Label>
              <Input
                id="customReason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter custom deactivation reason"
              />
            </div>
          )}

          {/* Confirmation Checkboxes */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="provider-cost"
                checked={providerCostConfirmed}
                onCheckedChange={(checked) => setProviderCostConfirmed(checked === true)}
              />
              <Label 
                htmlFor="provider-cost" 
                className="text-sm leading-5 cursor-pointer"
              >
                I have notified the provider(s) and confirmed no additional costs will be incurred after deactivation
              </Label>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox 
                id="smartlead-removal"
                checked={smartleadRemovalConfirmed}
                onCheckedChange={(checked) => setSmartleadRemovalConfirmed(checked === true)}
              />
              <Label 
                htmlFor="smartlead-removal" 
                className="text-sm leading-5 cursor-pointer"
              >
                I understand this will remove mailboxes from Smartlead and mark them as DEACTIVATED in the database
              </Label>
            </div>
          </div>

          {/* Warning Message */}
          <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-orange-800">
              <strong>Warning:</strong> This action cannot be undone. Deactivated mailboxes will be removed from active campaigns and stopped from sending emails.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={!isFormValid}
          >
            Deactivate {selectedCount} Mailboxes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}